/* eslint-disable max-len */
import { useField, useFormikContext } from 'formik';
import { ReactElement, useEffect, useState } from 'react';
import { abortPromiseOnUnmount } from 'src/services/base.service';
import { GradesAndPromotionsFormViewModel } from 'src/types/GradesAndPromotionsFormViewModel';
import FormDropdown from '../../../components/FormInputs/FormDropdown/FormDropdown';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  clearSearchError,
  clearSearchResults,
  selectGradesAndPromotionsFormViewModel,
  selectGradesAndPromotionsHasSearchMatch,
  selectGradesAndPromotionsLoadingGradesAndPromotions,
  selectGradesAndPromotionsSearchError,
  selectGradesAndPromotionsSearchResults,
  selectSchoolYear,
  setHasSearchMatch,
} from '../../../redux/slices/gradesAndPromotionsSlice';
import { fetchStudentByMSISId } from '../../../redux/thunks/gradesAndPromotionsThunks';
import { ButtonSize } from '../../../types/ButtonSize';
import { DataEntryMode } from '../../../types/DataEntryMode';
import { GradesAndPromotionsModel } from '../../../types/GradesAndPromotionsModel';
import { PanelType } from '../../../types/PanelType';
import { FormDropdownChangeEventConfig } from '../../../types/propTypes/FormDropdownPropTypes';
import { GradesAndPromotionsValidationSchema } from '../../../types/Validation/GradesAndPromotionsValidationSchema';
import ActionButton from '../../ActionButton/ActionButton';
import FormDateSelector from '../../FormInputs/FormDateSelector/FormDateSelector';
import FormTextbox from '../../FormInputs/FormTextbox/FormTextbox';
import Panel from '../../Panel/Panel';
import './GradesAndPromotions_StudentDetailsPanel.css';
import { MAX_DATE } from 'src/types/Constants';

const GradesAndPromotions_StudentDetailsPanel = (): ReactElement => {
  const { values, setFieldValue, setFieldTouched } =
    useFormikContext<GradesAndPromotionsModel>();
  const dispatch = useAppDispatch();

  const [, msisIdMetaProps] = useField('studentIdentifierState');
  const getIsMsisIDValid = (): boolean => !msisIdMetaProps?.error;

  const formView: GradesAndPromotionsFormViewModel = useAppSelector(
    selectGradesAndPromotionsFormViewModel
  );

  const [studentUpdate, setStudentUpdate] = useState(false);
  const newModeSchoolYear = useAppSelector(selectSchoolYear);
  const searching = useAppSelector(
    selectGradesAndPromotionsLoadingGradesAndPromotions
  );
  const hasSearchMatch = useAppSelector(
    selectGradesAndPromotionsHasSearchMatch
  );
  const searchMessage = useAppSelector(selectGradesAndPromotionsSearchError);
  const gradesAndPromotionsData = useAppSelector(
    selectGradesAndPromotionsSearchResults
  );
  const [isGraduationDateRequired, setIsGraduationDateRequired] =
    useState(false);

  // Update form fields when Redux state changes
  useEffect(() => {
    if (
      hasSearchMatch &&
      gradesAndPromotionsData &&
      gradesAndPromotionsData.studentFirstName
    ) {
      const setUserFields = (data: GradesAndPromotionsModel): void => {
        let schoolYear = '';
        if (DataEntryMode.NEW) {
          schoolYear = newModeSchoolYear || formView.defaultSchoolYear;
        }

        setFieldValue('id', data.id, true);
        setFieldValue('studentIdentifier', data.studentIdentifier, true);
        setFieldValue('studentFirstName', data.studentFirstName, true);
        setFieldValue('studentLastOrSurname', data.studentLastOrSurname, true);
        /* istanbul ignore next */
        setFieldValue('studentMiddleName', data.studentMiddleName || '', true);
        setFieldValue('birthDate', data.birthDate, true);
        /* istanbul ignore next */
        setFieldValue('districtName', data.districtName, true);
        setFieldValue('leaIdentifier', data.leaIdentifier, true);
        setFieldValue('districtId', data.leaIdentifier, true);
        if (data.leaIdentifier)
          setFieldValue(
            'district',
            `${data.districtName} (${data.leaIdentifier})`,
            true
          );
        setFieldValue('schoolYear', schoolYear, true);
        setFieldValue('schoolName', data.schoolName, true);
        setFieldValue('schoolId', data.schoolId, true);
        if (data.schoolId)
          setFieldValue(
            'school',
            `${data.schoolName} (${data.schoolId})`,
            true
          );
        /* istanbul ignore next */
        setFieldValue('entryGradeLevel', data.entryGradeLevel || '', true);
        /* istanbul ignore next */
        setFieldValue('promotionStatus', data.promotionStatus, true);
        setFieldValue(
          'highSchoolDiplomaType',
          data.highSchoolDiplomaType,
          true
        );
        setFieldValue(
          'diplomaOrCredentialAwardDate',
          data.diplomaOrCredentialAwardDate,
          true
        );
        setFieldValue('enrollmentEntryDate', data.enrollmentEntryDate, true);
        setFieldValue('courses', data.courses, true);
        setFieldValue('studentUpdate', data.studentUpdate, true);
        setFieldValue('hasMatch', true);
        setStudentUpdate(data.studentUpdate);
      };

      setUserFields(gradesAndPromotionsData);
    }
  }, [
    hasSearchMatch,
    gradesAndPromotionsData,
    setFieldValue,
    newModeSchoolYear,
    formView.defaultSchoolYear,
  ]);

  const searchDisabled =
    !getIsMsisIDValid() || searching || hasSearchMatch || searchMessage !== '';

  const updateGraduationType = async (
    config: FormDropdownChangeEventConfig
  ): Promise<void> => {
    const isRequired =
      config.value == '' || config.value == undefined ? false : true;
    setIsGraduationDateRequired(isRequired);

    /* istanbul ignore next */
    if (config.setValueHook && isRequired) {
      config.setValueHook('diplomaOrCredentialAwardDate', '');
    }
  };

  const handleMSISSearch = async (): Promise<void> => {
    dispatch(
      fetchStudentByMSISId({
        msisId: values.studentIdentifier,
        schoolYear: newModeSchoolYear || formView.defaultSchoolYear,
      })
    );
  };

  const handleMsisValueChange = (): Promise<void> => {
    const resetMatchedFields = (): void => {
      setFieldValue('studentFirstName', '', true);
      setFieldValue('studentLastOrSurname', '', true);
      setFieldValue('studentMiddleName', '', true);
      setFieldValue('birthDate', '', true);
      setFieldValue('districtName', '', true);
      setFieldValue('leaIdentifier', '', true);
      setFieldValue('districtId', '', true);
      setFieldValue('district', '', true);
      setFieldValue('schoolYear', newModeSchoolYear, true);
      setFieldValue('school', '', true);
      setFieldValue('schoolName', '', true);
      setFieldValue('schoolId', '', true);
      /* istanbul ignore next */
      setFieldValue('entryGradeLevel', '', true);
      /* istanbul ignore next */
      setFieldValue('promotionStatus', '', true);
      setFieldValue('highSchoolDiplomaType', '', true);
      setFieldValue('diplomaOrCredentialAwardDate', '', true);
      setFieldValue('enrollmentEntryDate', '', true);
      setFieldValue('courses', [], true);
      setFieldValue('hasMatch', false);

      setFieldTouched('studentIdentifier', false);
      setFieldTouched('studentFirstName', false);
      setFieldTouched('studentLastOrSurname', false);
      setFieldTouched('birthDate', false);
      setFieldTouched('districtName', false);
      setFieldTouched('leaIdentifier', false);
      setFieldTouched('lea', false);
      setFieldTouched('schoolYear', false);
      setFieldTouched('schoolId', false);
      setFieldTouched('schoolName', false);
      setFieldTouched('school', false);
      setFieldTouched('entryGradeLevel', false);
      setFieldTouched('promotionStatus', false);
      setFieldTouched('highSchoolDiplomaType', false);
      setFieldTouched('diplomaOrCredentialAwardDate', false);
      setFieldTouched('enrollmentEntryDate', false);
    };

    dispatch(clearSearchError());
    dispatch(clearSearchResults());

    // Change after match
    if (hasSearchMatch) {
      resetMatchedFields();
      dispatch(setHasSearchMatch(false));
    }

    return Promise.resolve();
  };

  useEffect(() => {
    let promise: unknown;
    return () => {
      dispatch(clearSearchResults());
      abortPromiseOnUnmount(promise);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  return (
    <>
      <Panel panelType={PanelType.INFO} heading="Student Information">
        <div className="field-row field-row-update-grades-msis-id">
          <div>
            <FormTextbox
              displayName="Enter MSIS ID"
              field="studentIdentifier"
              validationSchema={GradesAndPromotionsValidationSchema}
              onChangeEvent={handleMsisValueChange}
            />
          </div>
          <div className="search-button">
            <ActionButton
              classes="button--secondary"
              size={ButtonSize.SMALL}
              onClick={handleMSISSearch}
              disabled={searchDisabled}
              dataTestId="student-search-button"
              tooltipText="Click to search for student"
              loading={searching}
            >
              Search
            </ActionButton>
          </div>
          <div className="school-year">
            <FormTextbox
              displayName="School Year"
              field="schoolYear"
              validationSchema={GradesAndPromotionsValidationSchema}
              disabled={true}
            />
          </div>
          <div className="search-message" data-testid="search-error-message">
            {searchMessage}
          </div>
        </div>

        <div className="field-row field-row-msis-search-results">
          <FormTextbox
            displayName="Last Name"
            field="studentLastOrSurname"
            disabled={true}
            validationSchema={GradesAndPromotionsValidationSchema}
          />
          <FormTextbox
            displayName="First Name"
            field="studentFirstName"
            disabled={true}
            validationSchema={GradesAndPromotionsValidationSchema}
          />
          <FormTextbox
            displayName="Middle Name"
            field="studentMiddleName"
            validationSchema={GradesAndPromotionsValidationSchema}
            disabled={true}
          />
          <FormDateSelector
            displayName="Date of Birth"
            field="birthDate"
            placeholderText=""
            disabled={true}
            validationSchema={GradesAndPromotionsValidationSchema}
            maxDate={MAX_DATE}
            isClearable
          />
        </div>
      </Panel>

      <Panel panelType={PanelType.INFO} heading="LEA & School Information">
        <div className="field-row field-row-update-grades-msis-search-results">
          <FormTextbox
            displayName="LEA"
            field="district"
            validationSchema={GradesAndPromotionsValidationSchema}
            disabled={true}
          />
          <FormTextbox
            displayName="School"
            field="school"
            validationSchema={GradesAndPromotionsValidationSchema}
            disabled={true}
          />
        </div>
        <div className="field-row field-row-msis-search-results"></div>
      </Panel>
      <Panel panelType={PanelType.INFO} heading="Update Promotions">
        <div className="field-row field-row-update-grades-results">
          <FormDropdown
            displayName="Promoted"
            field="promotionStatus"
            options={formView?.promotedOptions ?? []}
            validationSchema={GradesAndPromotionsValidationSchema}
            disabled={!studentUpdate}
          />
          <FormDropdown
            displayName="Graduation Type"
            field="highSchoolDiplomaType"
            isClearable={true}
            options={formView?.graduationTypeOptions ?? []}
            onChangeEvent={updateGraduationType}
            validationSchema={GradesAndPromotionsValidationSchema}
            disabled={!studentUpdate}
          />
          <FormDateSelector
            displayName="Graduation Date"
            field="diplomaOrCredentialAwardDate"
            validationSchema={GradesAndPromotionsValidationSchema}
            placeholderText="mm/dd/yyyy"
            classConfig={{
              labelClasses: isGraduationDateRequired
                ? 'forced-required-label'
                : '',
            }}
            disabled={!studentUpdate}
            maxDate={MAX_DATE}
            isClearable
          />
          <FormTextbox
            displayName="Grade Level"
            field="entryGradeLevel"
            validationSchema={GradesAndPromotionsValidationSchema}
            disabled={true}
          />
          <FormDateSelector
            displayName="Enrollment Date"
            field="enrollmentEntryDate"
            validationSchema={GradesAndPromotionsValidationSchema}
            placeholderText="mm/dd/yyyy"
            disabled={true}
            maxDate={MAX_DATE}
            isClearable
          />
        </div>
      </Panel>
    </>
  );
};

export default GradesAndPromotions_StudentDetailsPanel;
